import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmt$, fmtPct } from "@/lib/format";
import { Lock } from "lucide-react";

export default function Compare() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const compareLimit = user?.entitlement?.compare_limit ?? 2;
  const isPremium = user?.entitlement?.plan_type === "premium";

  useEffect(() => { if (!loading && !user) nav("/login?next=/compare"); }, [loading, user, nav]);
  useEffect(() => { if (user) api.get("/properties").then(r => setItems(r.data)); }, [user]);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : s.length < compareLimit ? [...s, id] : s);
  const chosen = items.filter(i => selected.includes(i.property_id));

  const rows = [
    ["Financing", i => (i.financing_type || "").toUpperCase()],
    ["Purchase Price", i => fmt$(i.results?.purchase_price)],
    ["Interest Rate", i => `${i.inputs?.interest_rate || 0}%`],
    ["Monthly Housing", i => fmt$(i.results?.monthly_total)],
    ["Cash Before Closing", i => fmt$(i.results?.cash_before_closing)],
    ["Cash at Closing", i => fmt$(i.results?.additional_cash_at_closing)],
    ["Max Transaction Cash", i => fmt$(i.results?.max_transaction_cash)],
    ["Cash Remaining", i => i.results?.cash_remaining != null ? fmt$(i.results.cash_remaining) : "—"],
    ["Annual Taxes", i => fmt$(Number(i.inputs?.annual_taxes || 0))],
    ["Annual Insurance", i => fmt$(Number(i.inputs?.annual_insurance || 0))],
    ["HOA (monthly)", i => fmt$(Number(i.inputs?.hoa_monthly || 0))],
    ["Estimated DTI", i => i.results?.dti != null ? fmtPct(i.results.dti) : "—"],
    ["Purchase Score", i => `${Math.round(i.results?.score || 0)} / 100`],
  ];

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14">
        <h1 className="font-display text-3xl mb-2">Compare Homes</h1>
        <p className="text-sm text-brand-muted mb-4">Select up to {compareLimit} saved homes to compare side by side.</p>
        {!isPremium && (
          <div data-testid="compare-upsell" className="mb-6 rounded-xl border border-brand-border bg-brand-soft/40 p-4 flex items-start gap-3 text-sm">
            <Lock size={16} className="text-brand-muted mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-brand-ink font-medium">Comparing 3 homes side-by-side is a Home Buying Pass feature.</div>
              <div className="text-xs text-brand-muted mt-0.5">Unlock 90 days for $29.99 (one-time, no auto-renewal).</div>
            </div>
            <button data-testid="compare-upgrade" onClick={() => nav("/upgrade?next=/compare&reason=compare")} className="btn-primary text-xs py-1.5 px-3">Upgrade</button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-8">
          {items.map(i => (
            <button key={i.property_id} data-testid={`sel-${i.property_id}`} onClick={() => toggle(i.property_id)}
              className={`text-left rounded-2xl border p-4 transition-colors ${selected.includes(i.property_id) ? "border-brand-accent bg-brand-soft/50" : "border-brand-border bg-white"}`}>
              <div className="font-medium">{i.nickname}</div>
              <div className="text-xs text-brand-muted mt-1">{fmt$(i.results?.monthly_total)} /mo</div>
            </button>
          ))}
          {items.length === 0 && <div className="text-brand-muted col-span-full">No saved homes yet. <Link to="/analyze" className="text-brand-accent">Analyze one</Link>.</div>}
        </div>

        {chosen.length >= 2 && (
          <div className="card-hs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-soft/50">
                  <tr>
                    <th className="text-left p-4 text-brand-muted font-medium">Metric</th>
                    {chosen.map(c => <th key={c.property_id} className="text-left p-4 text-brand-ink font-medium">{c.nickname}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([k, fn]) => (
                    <tr key={k} className="border-t border-brand-border">
                      <td className="p-4 text-brand-muted">{k}</td>
                      {chosen.map(c => <td key={c.property_id} className="p-4 text-brand-ink">{fn(c)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
